// pages/IngredientsPage.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { listIngredients } from '../api/endpoints';
import type { Ingredient, IngredientListResponse } from '../types/api';
import { getErrorMessage } from '../utils/errors';

const IconSearch = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.3-4.3" />
    </svg>
);

const ALL_FUNCTIONS = [
    'antioxidant', 'emollient', 'conditioner',
    'humectant', 'surfactant', 'emulsifier',
    'solvent', 'fragrance', 'film_former',
];

type AttrKey = 'vegan' | 'halal';

const ATTR_FILTERS: { key: AttrKey; label: string }[] = [
    { key: 'vegan', label: 'Vegan' },
    { key: 'halal', label: 'Halal' },
];

export default function IngredientsPage(): React.JSX.Element {
    const [items, setItems] = useState<Ingredient[]>([]);
    const [meta, setMeta] = useState<{
        total: number; offset: number; limit: number; next?: number | null;
    }>({ total: 0, offset: 0, limit: 30 });
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [searchName, setSearchName] = useState('');
    const [selectedFunctions, setSelectedFunctions] = useState<string[]>([]);
    const [attrs, setAttrs] = useState<Record<AttrKey, boolean>>({
        vegan: false, halal: false,
    });

    const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const load = useCallback(async (offset = 0, append = false) => {
        if (append) setLoadingMore(true); else setLoading(true);
        setError(null);
        try {
            const res: IngredientListResponse = await listIngredients({
                limit: meta.limit,
                offset,
                name: searchName,
                functions: selectedFunctions,
                vegan: attrs.vegan || undefined,
                halal: attrs.halal || undefined,
            });
            setMeta({ total: res.total, offset: res.offset, limit: res.limit, next: res.next_offset ?? null });
            setItems(prev => append ? [...prev, ...res.items] : res.items);
        } catch (error_: unknown) {
            setError(getErrorMessage(error_, 'Failed to load ingredients'));
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [meta.limit, searchName, selectedFunctions, attrs]);

    useEffect(() => { void load(0, false); }, [load, selectedFunctions, attrs]);

    useEffect(() => {
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => { void load(0, false); }, 800);
        return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
    }, [searchName, load]);

    const toggleFunction = (fn: string) =>
        { setSelectedFunctions(prev =>
            prev.includes(fn) ? prev.filter(f => f !== fn) : [...prev, fn]
        ); };

    const toggleAttr = (key: AttrKey) =>
        { setAttrs(prev => ({ ...prev, [key]: !prev[key] })); };

    return (
        <div className="ing-page">
            {/* Page header */}
            <div className="card card--xl card--bordered card--flat scan-pattern ing-page__header">
                <div className="stack-5">
                    <div className="stack-3">
                        <span className="t-label">Ingredient library</span>
                        <h1 className="t-h1 ing-page__title">
                            Ingredients <em className="t-italic">database</em>
                        </h1>
                        <p className="t-body t-mute ing-page__lead">
                            A comprehensive library of cosmetic ingredients from the CosIng database, enriched with formulation context and ingredient information.
                        </p>
                    </div>

                    <div className="field-group">
                        <span className="field-group__icon" aria-hidden="true">
                            <IconSearch />
                        </span>
                        <input
                            className="field"
                            type="text"
                            placeholder="Search by name or INCI…"
                            value={searchName}
                            onChange={e => { setSearchName(e.target.value); }}
                            aria-label="Search ingredients"
                        />
                    </div>

                    <div className="ing-page__filters">
                        <div className="row-3 ing-page__filter-row">
                            {ALL_FUNCTIONS.map(fn => (
                                <button
                                    key={fn}
                                    onClick={() => { toggleFunction(fn); }}
                                    className={`pill pill--filter${selectedFunctions.includes(fn) ? ' is-active' : ''}`}
                                >
                                    {fn.replace('_', ' ')}
                                </button>
                            ))}
                            <div className="ing-page__filter-sep" aria-hidden="true" />
                            {ATTR_FILTERS.map(({ key, label }) => (
                                <button
                                    key={key}
                                    onClick={() => { toggleAttr(key); }}
                                    className={`pill pill--filter${attrs[key] ? ' is-active' : ''}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        <p className="t-small t-mute">Preference filters — not medical advice.</p>
                    </div>
                </div>
            </div>

            {/* Loading skeleton */}
            {loading && !loadingMore && (
                <div className="ing-grid">
                    {[1, 2, 3, 4, 5, 6].map(i => (
                        <div key={i} className="card card--bordered card--flat ing-page__skeleton" />
                    ))}
                </div>
            )}

            {/* Error */}
            {error && (
                <div className="notice notice--error">{error}</div>
            )}

            {/* Results grid */}
            {!loading && !error && (
                <div className="ing-grid">
                    {items.map(ing => <IngredientCard key={ing.id} ing={ing} />)}
                    {items.length === 0 && (
                        <div className="ing-page__empty">
                            <div className="icon-ring">
                                <IconSearch />
                            </div>
                            <p className="t-body t-mute">No ingredients match your search.</p>
                        </div>
                    )}
                </div>
            )}

            {/* Load more */}
            {meta.next != null && (
                <div className="ing-page__loadmore">
                    <button
                        className="btn btn--ghost"
                        onClick={() => { void load(meta.next ?? undefined, true); }}
                        disabled={loadingMore}
                    >
                        {loadingMore ? 'Loading…' : 'Load more'}
                    </button>
                </div>
            )}
        </div>
    );
}

function IngredientCard({ ing }: { ing: Ingredient }) {
    const dotClass = useMemo(() => {
        const level = ing.irritant_level.toLowerCase();
        return (level === 'high' || level === 'moderate') ? 'dot--coral' : '';
    }, [ing.irritant_level]);

    const riskLabel = ing.irritant_level
        ? ing.irritant_level.charAt(0).toUpperCase() + ing.irritant_level.slice(1).toLowerCase()
        : 'Low';

    return (
        <div className="card card--bordered ing-card">
            <div className="row-3 ing-card__meta">
                <span className="pill">
                    <span className={`dot${dotClass ? ' ' + dotClass : ''}`} />
                    {riskLabel} concern
                </span>
                {ing.vegan_possible && (
                    <span className="pill pill--teal ing-card__vegan">Vegan</span>
                )}
            </div>

            <div className="stack-2 ing-card__name">
                <h3 className="t-h3 ing-card__heading">
                    <Link
                        to={`/ingredients/${ing.id}`}
                        className="link--subtle"
                    >
                        {ing.normalized_name}
                    </Link>
                </h3>
                <p className="t-label t-mute ing-card__inci">{ing.inci_name}</p>
            </div>

            {ing.functions.length > 0 && (
                <div className="ing-card__tags">
                    {ing.functions.slice(0, 3).map(f => (
                        <span key={f} className="pill">{f.toLowerCase()}</span>
                    ))}
                    {ing.functions.length > 3 && (
                        <span className="t-small t-mute">+{ing.functions.length - 3}</span>
                    )}
                </div>
            )}

            <div className="ing-card__cta">
                <Link
                    to={`/ingredients/${ing.id}`}
                    className="btn btn--ghost btn--sm btn--full"
                >
                    View details
                </Link>
            </div>
        </div>
    );
}
